import { isEqual, isObject, memoize } from 'lodash-es';

import { inputDebounceTimeout } from '@iconik-io/core/src/constants/debounce';
import { debouncePromise } from '@iconik-io/core/src/debounce_promise';
import { parsePositiveInteger } from '@iconik-io/core/src/parse_positive_integer';
import type { EndpointId } from '@iconik-io/iconik-api/src/client/request';
import { EndpointError } from '@iconik-io/iconik-api/src/client/response';

import type { SortOption } from '../../utils/sort/types';
import { getCommonFiltersSlice } from '../common_filters/slice';
import type { FacetValues } from '../common_filters/types';
import {
  httpCancelRequests,
  isCancellation,
} from '../middlewares/http/actions';
import type { EndpointResponseNormalized } from '../middlewares/http_normalizer/types';
import { fromEndpointError, fromError } from '../serializable_error';
import type { AppDispatch } from '../types';
import { getCommonListSlice } from './slice';
import type { FacetSchema, FacetsSchema } from './types';

export type CommonListGetParams = {
  sort?: SortOption[];
  page?: number;
  per_page?: number;
  initialFacets?: FacetsSchema;
  initialFacetsValues?: FacetValues;
  search_after?: unknown[];
};

export type CommonListGetConfig = {
  debounce?: boolean;
  // Debounce dependencies allow to specify which requests should be triggered
  // cancelled if there are any in progress and debounce queue is starting.
  debounceDependencies?: EndpointId[];
  paginationType?: 'infinite' | 'regular' | 'cursor';
};

export type CommonListGetOptions<Result> = {
  listName: string;
  dispatch: AppDispatch;
  getList: (
    dispatch: AppDispatch,
    params: CommonListGetParams,
  ) => Promise<Result>;
  params: CommonListGetParams;
  config?: CommonListGetConfig;
};

// Memoization of debounced function by _listName is necessary for wrapCommonGetList
// as it handles creation of only one instance of a debounced function per key.
// Otherwise, we would get a new debounced function every wrapCommonGetList call,
// and such debouncing wouldn't make sense.
const memoizedDebounced = memoize(
  <Result extends EndpointResponseNormalized>(_listName: string) =>
    debouncePromise(
      (
        fn: (
          dispatch: AppDispatch,
          params: CommonListGetParams,
        ) => Promise<Result>,
        dispatch: AppDispatch,
        params: CommonListGetParams,
      ) => fn(dispatch, params),
      inputDebounceTimeout,
    ),
);

const debounceProcessing: { [listName: string]: boolean } = {};

/**
 * Runs a list fetch and owns the whole lifecycle of it: the fetch window, the
 * recorded error and the settled status. Anything a list load needs to do before
 * its request — fetching the entity being listed, applying its stored criteria —
 * belongs inside `getList` so that it is covered too.
 *
 * Resolves with `undefined` when the request was superseded and cancelled: that
 * is an internal signal, not an outcome, and a caller settling the load on it
 * would let an empty id list render as "no results" while the newer request is
 * still on the wire. A real failure still rejects, so a caller can react to it
 * (the error is also recorded in the slice).
 */
export const commonGetList = <Result extends EndpointResponseNormalized>({
  listName,
  dispatch,
  params,
  getList,
  config = {},
}: CommonListGetOptions<Result>): Promise<Result | undefined> => {
  const {
    debounce,
    debounceDependencies,
    paginationType = 'infinite',
  } = config;
  const { actions } = getCommonListSlice(listName);
  const filtersActions = getCommonFiltersSlice(listName).actions;
  if (params.sort) {
    dispatch(actions.setSort(params.sort));
  }
  dispatch(actions.setFetchStatus('fetching'));
  dispatch(actions.clearError());

  // Reset pagination on a fresh fetch (an explicit page 1 or a cursor reset) so
  // a stale infinite-scroll handler cannot request a page that only existed for
  // the previous query. Ids are left intact to avoid flicker; the first page
  // response replaces them.
  const isInitialPage = params.page === undefined || params.page === 1;
  const isFreshList =
    paginationType === 'cursor' ? !params.search_after : isInitialPage;
  const shouldResetPagination = paginationType !== 'regular' && isFreshList;
  if (shouldResetPagination) {
    dispatch(actions.resetPagination());
  }

  let shouldDebounce = debounce;
  // Debounced list fetching normally is triggered by continuous user's input,
  // we don't want to mess up such calls with non-debounced ones, so we are
  // forcing them to be debounced ones as well.
  if (!debounce && debounceProcessing[listName]) {
    shouldDebounce = true;
  }

  if (shouldDebounce) {
    debounceProcessing[listName] = true;
    // Cancel all dependent requests that might be in progress.
    if (debounceDependencies) {
      dispatch(httpCancelRequests(debounceDependencies));
    }
  }
  let cancelled = false;
  let failed = false;
  const fetchPromise = shouldDebounce
    ? memoizedDebounced<Result>(listName)(getList, dispatch, params)
    : getList(dispatch, params);
  return fetchPromise
    .then((result: Result) => {
      const { body, meta } = result;
      if (!meta?.normalizedMultiple) {
        return result;
      }
      if (!isObject(body)) {
        return result;
      }
      const isFreshFetch =
        ('page' in body && body.page === 1) ||
        paginationType === 'regular' ||
        (paginationType === 'cursor' && !params.search_after);

      if (isFreshFetch) {
        dispatch(actions.setIds(meta.normalizedMultiple.result));
      } else {
        dispatch(actions.appendIds(meta.normalizedMultiple.result));
      }

      if (paginationType === 'cursor') {
        const objects =
          'objects' in body && Array.isArray(body.objects) ? body.objects : [];
        const lastObject = objects.at(-1);
        const nextCursor =
          typeof lastObject === 'object' &&
          lastObject !== null &&
          '_sort' in lastObject &&
          Array.isArray(lastObject._sort)
            ? lastObject._sort
            : undefined;
        dispatch(actions.setNextSearchAfter(nextCursor));
      }

      if ('facets' in body && body.facets) {
        // biome-ignore lint/plugin: facets are typed per search endpoint; this list thunk handles them uniformly.
        const facets = body.facets as FacetsSchema;
        const initialFacetValuesKeys = Object.keys(
          params?.initialFacetsValues ?? {},
        );

        const initialBuckets = Object.keys(params.initialFacets ?? {})
          .filter((item) => initialFacetValuesKeys.includes(item))
          .map((item) => params.initialFacets?.[item].buckets);
        const newBuckets = Object.keys(facets ?? {})
          .filter((item) => initialFacetValuesKeys.includes(item))
          .map((item) => facets?.[item].buckets);

        let currentFacets: FacetsSchema['current_facet_filters'] | undefined;
        if ('current_facet_filters' in facets && facets.current_facet_filters) {
          if (!isEqual(newBuckets, initialBuckets)) {
            const cachedFacetValues: {
              [key: string]: FacetSchema;
            } = {};
            initialFacetValuesKeys.forEach((key) => {
              cachedFacetValues[key] = {
                ...facets[key],
                buckets: params.initialFacets?.[key]?.buckets,
              };
            });

            currentFacets = {
              ...facets.current_facet_filters,
              ...cachedFacetValues,
            };
          } else {
            currentFacets = facets.current_facet_filters;
          }
        }

        dispatch(
          filtersActions.setAvailableFacets({ ...facets, ...currentFacets }),
        );
      }
      dispatch(
        actions.setPagination({
          page: 'page' in body ? (parsePositiveInteger(body.page) ?? 1) : 1,
          pages: 'pages' in body ? (parsePositiveInteger(body.pages) ?? 1) : 1,
          total: 'total' in body && body.total ? Number(body.total) : 0,
          per_page:
            'per_page' in body && body.per_page ? Number(body.per_page) : 0,
        }),
      );

      return result;
    })
    .catch((error) => {
      if (isCancellation(error)) {
        cancelled = true;
        return undefined;
      }
      failed = true;
      // A fetch that would have replaced the list leaves nothing behind that
      // still describes it, so drop the previous query's items rather than show
      // them as this one's result. A later page of an infinite list is additive
      // and leaves what is already loaded alone.
      if (paginationType === 'regular' || isFreshList) {
        dispatch(actions.setIds([]));
        dispatch(actions.resetPagination());
      }
      if (error instanceof EndpointError) {
        dispatch(actions.setError(fromEndpointError(error)));
      } else {
        dispatch(actions.setError(fromError(error)));
      }
      throw error;
    })
    .finally(() => {
      // Released even for a cancelled request: leaving it set would force every
      // later fetch of this list through the debounce timeout.
      debounceProcessing[listName] = false;
      // A cancelled request was superseded by a newer one that now owns the
      // fetch status, so leave it untouched to avoid a reload loop.
      if (cancelled) {
        return;
      }
      // Status before fetch status: the two are separate store updates, and an
      // intermediate render must never show a settled status while the list is
      // still considered empty by a consumer waiting on the fetch.
      dispatch(actions.setStatus(failed ? 'error' : 'success'));
      dispatch(actions.setFetchStatus('idle'));
    });
};

export type CommonListInsertAt = 'start' | 'end';

export type CommonListCreateOptions<Result> = {
  dispatch: AppDispatch;
  listName: string;
  insertAt?: CommonListInsertAt;
  createPromise: Promise<Result>;
};

export const commonCreate = <Result extends EndpointResponseNormalized>({
  listName,
  dispatch,
  insertAt = 'start',
  createPromise,
}: CommonListCreateOptions<Result>): Promise<Result> =>
  createPromise.then((result) => {
    const { meta } = result;
    if (!meta?.normalizedSingular) {
      return result;
    }
    const { actions } = getCommonListSlice(listName);
    switch (insertAt) {
      case 'start':
        dispatch(actions.prependId(meta.normalizedSingular.result));
        break;
      case 'end':
        dispatch(actions.appendId(meta.normalizedSingular.result));
        break;
      default:
        break;
    }

    return result;
  });

export type CommonListDeleteOptions = {
  dispatch: AppDispatch;
  listName: string;
  ids: string[];
  deletePromise: Promise<unknown>;
};

export const commonDelete = ({
  listName,
  dispatch,
  ids,
  deletePromise,
}: CommonListDeleteOptions): Promise<void> =>
  deletePromise.then(() => {
    const { actions } = getCommonListSlice(listName);

    dispatch(actions.deleteIds(ids));
  });
